import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  BarChart3,
  Hash,
  Lock,
  MessageSquarePlus,
  Plus,
  Search,
  Send,
  Trash2,
  Trophy,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { initials } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const REACTION_EMOJI = {
  like: "👍",
  celebrate: "🎉",
  thanks: "🙏",
  clap: "👏",
  fire: "🔥",
};
const BADGE_LABEL = {
  team_player: "Team Player",
  above_beyond: "Above & Beyond",
  customer_hero: "Customer Hero",
  innovator: "Innovator",
  closer: "Closer",
  mentor: "Mentor",
};

function ChannelIcon({ channel }) {
  if (channel.type === "dm") return <Users className="h-4 w-4" />;
  if (channel.type === "group") return <Users className="h-4 w-4" />;
  if (channel.visibility === "private") return <Lock className="h-4 w-4" />;
  return <Hash className="h-4 w-4" />;
}

function channelLabel(channel) {
  return channel.display_name || channel.name || "Direct message";
}

export default function ChannelsPanel({ currentUser }) {
  const [searchParams] = useSearchParams();
  const [channels, setChannels] = useState(null);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [options, setOptions] = useState({ people: [], badges: [], reactions: [] });
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [newChannel, setNewChannel] = useState(false);
  const [newDm, setNewDm] = useState(false);
  const [pollOpen, setPollOpen] = useState(false);
  const [shoutOpen, setShoutOpen] = useState(false);
  const scrollRef = useRef(null);

  const loadChannels = useCallback(async () => {
    try {
      const { data } = await api.get("/channels");
      setChannels(data.items || []);
      return data.items || [];
    } catch (error) {
      toast.error(apiErr(error));
      setChannels([]);
      return [];
    }
  }, []);

  const loadMessages = useCallback(async (channelId) => {
    try {
      const { data } = await api.get(`/channels/${channelId}/messages`);
      setMessages(data.items || []);
    } catch (error) {
      toast.error(apiErr(error));
    }
  }, []);

  useEffect(() => {
    api.get("/channels/options").then(({ data }) => setOptions(data)).catch(() => {});
    loadChannels().then((items) => {
      const wanted = searchParams.get("channel");
      const target = items.find((c) => c.id === wanted) || items[0];
      if (target) setActive(target);
    });
  }, [loadChannels, searchParams]);

  useEffect(() => {
    if (active) loadMessages(active.id);
  }, [active, loadMessages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const refresh = async () => {
    if (active) await loadMessages(active.id);
    await loadChannels();
  };

  const send = async () => {
    if (!text.trim() || !active || busy) return;
    setBusy(true);
    try {
      await api.post(`/channels/${active.id}/messages`, { body: text.trim() });
      setText("");
      await refresh();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusy(false);
    }
  };

  const react = async (messageId, reaction) => {
    if (!active) return;
    try {
      await api.post(`/channels/${active.id}/messages/${messageId}/react`, { reaction });
      await loadMessages(active.id);
    } catch (error) {
      toast.error(apiErr(error));
    }
  };

  const vote = async (messageId, optionId) => {
    if (!active) return;
    try {
      await api.post(`/channels/${active.id}/messages/${messageId}/vote`, { option_id: optionId });
      await loadMessages(active.id);
    } catch (error) {
      toast.error(apiErr(error));
    }
  };

  const removeMessage = async (messageId) => {
    if (!active) return;
    try {
      await api.delete(`/channels/${active.id}/messages/${messageId}`);
      await loadMessages(active.id);
    } catch (error) {
      toast.error(apiErr(error));
    }
  };

  if (channels === null) {
    return <div className="py-20 text-center text-sm text-muted-foreground">Loading channels…</div>;
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[300px_1fr]" data-testid="channels-panel">
      <Card className="flex max-h-[640px] flex-col overflow-hidden p-0">
        <div className="flex items-center justify-between gap-2 border-b border-border p-3">
          <span className="font-display text-sm font-semibold">Channels & DMs</span>
          <div className="flex gap-1">
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setNewDm(true)}
              data-testid="channels-new-dm"
              title="New direct message"
            >
              <MessageSquarePlus className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setNewChannel(true)}
              data-testid="channels-new-channel"
              title="Create channel"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {channels.length === 0 && (
            <div className="p-4 text-xs text-muted-foreground">
              No channels yet. Create one or start a DM.
            </div>
          )}
          {channels.map((channel) => (
            <button
              key={channel.id}
              onClick={() => setActive(channel)}
              data-testid={`channel-item-${channel.id}`}
              className={`flex w-full items-center gap-2 border-b border-border/60 px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted ${
                active?.id === channel.id ? "bg-[#255058]/10" : ""
              }`}
            >
              <span className="text-[#255058]">
                <ChannelIcon channel={channel} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{channelLabel(channel)}</span>
                {channel.last_message && (
                  <span className="block truncate text-xs text-muted-foreground">
                    {channel.last_message.author_name}: {channel.last_message.body?.slice(0, 40) || "…"}
                  </span>
                )}
              </span>
              {channel.unread > 0 && (
                <Badge className="bg-[#255058] text-white" data-testid={`channel-unread-${channel.id}`}>
                  {channel.unread}
                </Badge>
              )}
            </button>
          ))}
        </div>
      </Card>

      <Card className="flex max-h-[640px] min-h-[480px] flex-col overflow-hidden p-0">
        {!active ? (
          <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
            Select a channel to start the conversation.
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between border-b border-border p-3">
              <div className="flex items-center gap-2">
                <span className="text-[#255058]"><ChannelIcon channel={active} /></span>
                <span className="font-display font-semibold">{channelLabel(active)}</span>
                {active.description && (
                  <span className="text-xs text-muted-foreground">— {active.description}</span>
                )}
              </div>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setPollOpen(true)}
                  data-testid="channel-new-poll"
                >
                  <BarChart3 className="mr-1 h-4 w-4" /> Poll
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShoutOpen(true)}
                  data-testid="channel-new-shoutout"
                >
                  <Trophy className="mr-1 h-4 w-4" /> Shout-out
                </Button>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-4" data-testid="channel-messages">
              {messages.length === 0 && (
                <div className="text-sm text-muted-foreground">No messages yet. Say hello 👋</div>
              )}
              {messages.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  msg={msg}
                  currentUser={currentUser}
                  reactions={options.reactions}
                  onReact={react}
                  onVote={vote}
                  onDelete={removeMessage}
                />
              ))}
            </div>

            <div className="flex items-end gap-2 border-t border-border p-3">
              <Textarea
                value={text}
                rows={1}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder={`Message ${channelLabel(active)}`}
                data-testid="channel-message-input"
                className="max-h-28 min-h-[40px] resize-none"
              />
              <Button
                size="icon"
                onClick={send}
                disabled={busy}
                data-testid="channel-message-send"
                className="shrink-0 bg-[#255058] text-white hover:bg-[#1c3d43]"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </>
        )}
      </Card>

      <NewChannelDialog
        open={newChannel}
        onOpenChange={setNewChannel}
        people={options.people}
        currentUser={currentUser}
        onCreated={async (c) => {
          await loadChannels();
          setActive(c);
        }}
      />
      <NewDmDialog
        open={newDm}
        onOpenChange={setNewDm}
        people={options.people}
        currentUser={currentUser}
        onCreated={async (c) => {
          await loadChannels();
          setActive(c);
        }}
      />
      {active && (
        <PollDialog
          open={pollOpen}
          onOpenChange={setPollOpen}
          channelId={active.id}
          onCreated={refresh}
        />
      )}
      {active && (
        <ShoutoutDialog
          open={shoutOpen}
          onOpenChange={setShoutOpen}
          channelId={active.id}
          people={options.people}
          badges={options.badges}
          currentUser={currentUser}
          onCreated={refresh}
        />
      )}
    </div>
  );
}

function MessageBubble({ msg, currentUser, reactions, onReact, onVote, onDelete }) {
  const mine = msg.author_id === currentUser?.id;
  return (
    <div className="group flex gap-2" data-testid={`channel-message-${msg.id}`}>
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#255058] text-xs font-semibold text-white">
        {initials(msg.author_name || "Nexreq")}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">{msg.author_name}</span>
          <span className="text-xs text-muted-foreground">
            {new Date(msg.created_at).toLocaleString()}
          </span>
          {mine && (
            <button
              onClick={() => onDelete(msg.id)}
              className="opacity-0 transition-opacity group-hover:opacity-100"
              data-testid={`channel-message-delete-${msg.id}`}
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-red-500" />
            </button>
          )}
        </div>

        {msg.type === "shoutout" ? (
          <div className="mt-1 rounded-lg border border-amber-300 bg-amber-50 p-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-700">
              <Trophy className="h-4 w-4" />
              {BADGE_LABEL[msg.shoutout?.badge] || "Shout-out"} → {(msg.shoutout?.to_names || []).join(", ")}
            </div>
            <p className="mt-1 text-sm text-amber-900">{msg.body}</p>
          </div>
        ) : msg.type === "poll" ? (
          <PollCard msg={msg} currentUser={currentUser} onVote={onVote} />
        ) : (
          <p className="mt-0.5 whitespace-pre-wrap text-sm text-foreground">{msg.body}</p>
        )}

        <div className="mt-1.5 flex flex-wrap items-center gap-1">
          {Object.entries(msg.reactions || {}).map(([reaction, users]) =>
            users.length ? (
              <button
                key={reaction}
                onClick={() => onReact(msg.id, reaction)}
                className={`rounded-full border px-2 py-0.5 text-xs ${
                  users.includes(currentUser?.id) ? "border-[#255058] bg-[#255058]/10" : "border-border"
                }`}
                data-testid={`reaction-${reaction}-${msg.id}`}
              >
                {REACTION_EMOJI[reaction] || "•"} {users.length}
              </button>
            ) : null,
          )}
          <div className="flex opacity-0 transition-opacity group-hover:opacity-100">
            {reactions.map((reaction) => (
              <button
                key={reaction}
                onClick={() => onReact(msg.id, reaction)}
                className="px-1 text-sm hover:scale-110"
                title={reaction}
              >
                {REACTION_EMOJI[reaction] || "•"}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PollCard({ msg, currentUser, onVote }) {
  const options = msg.poll?.options || [];
  const total = options.reduce((sum, o) => sum + (o.votes?.length || 0), 0);
  return (
    <div className="mt-1 rounded-lg border border-border bg-card p-3" data-testid={`poll-${msg.id}`}>
      <div className="flex items-center gap-2 text-sm font-semibold">
        <BarChart3 className="h-4 w-4 text-[#255058]" /> {msg.body}
      </div>
      <div className="mt-2 space-y-2">
        {options.map((opt) => {
          const count = opt.votes?.length || 0;
          const pct = total ? Math.round((count / total) * 100) : 0;
          const voted = (opt.votes || []).includes(currentUser?.id);
          return (
            <button
              key={opt.id}
              onClick={() => onVote(msg.id, opt.id)}
              data-testid={`poll-option-${opt.id}`}
              className="relative w-full overflow-hidden rounded-md border border-border px-3 py-1.5 text-left text-sm"
            >
              <span
                className="absolute inset-y-0 left-0 bg-[#255058]/15"
                style={{ width: `${pct}%` }}
              />
              <span className="relative flex justify-between">
                <span className={voted ? "font-semibold text-[#255058]" : ""}>{opt.text}</span>
                <span className="text-xs text-muted-foreground">{count} · {pct}%</span>
              </span>
            </button>
          );
        })}
      </div>
      <div className="mt-1 text-xs text-muted-foreground">{total} vote(s)</div>
    </div>
  );
}

function PersonPicker({ people, currentUser, selected, setSelected }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(
    () =>
      people
        .filter((p) => p.id !== currentUser?.id)
        .filter((p) => p.name?.toLowerCase().includes(q.toLowerCase())),
    [people, q, currentUser],
  );
  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  return (
    <div>
      <div className="relative">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people" className="pl-8" data-testid="person-picker-search" />
      </div>
      <div className="mt-2 max-h-44 overflow-y-auto rounded-md border border-border">
        {filtered.map((p) => (
          <button
            key={p.id}
            onClick={() => toggle(p.id)}
            data-testid={`person-option-${p.id}`}
            className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted ${
              selected.includes(p.id) ? "bg-[#255058]/10" : ""
            }`}
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#255058] text-[10px] text-white">
              {initials(p.name)}
            </span>
            <span className="flex-1 truncate">{p.name}</span>
            {selected.includes(p.id) && <Badge className="bg-[#255058] text-white">✓</Badge>}
          </button>
        ))}
      </div>
    </div>
  );
}

function NewChannelDialog({ open, onOpenChange, people, currentUser, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [priv, setPriv] = useState(false);
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/channels", {
        name: name.trim(),
        description: description.trim(),
        visibility: priv ? "private" : "open",
        member_ids: priv ? members : [],
      });
      toast.success("Channel created");
      onOpenChange(false);
      setName(""); setDescription(""); setPriv(false); setMembers([]);
      onCreated(data);
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-channel-dialog">
        <DialogHeader><DialogTitle>Create channel</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Channel name (e.g. general)" data-testid="new-channel-name" />
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What's this channel about?" />
          <div className="flex items-center gap-2">
            <Switch id="private-ch" checked={priv} onCheckedChange={setPriv} data-testid="new-channel-private" />
            <Label htmlFor="private-ch">Private (invite only)</Label>
          </div>
          {priv && <PersonPicker people={people} currentUser={currentUser} selected={members} setSelected={setMembers} />}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="new-channel-submit" className="bg-[#255058] text-white hover:bg-[#1c3d43]">Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewDmDialog({ open, onOpenChange, people, currentUser, onCreated }) {
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const start = async () => {
    if (selected.length === 0) return;
    setSaving(true);
    try {
      if (selected.length === 1) {
        const { data } = await api.post("/channels/dm", { user_id: selected[0] });
        onCreated(data);
      } else {
        const { data } = await api.post("/channels/group", {
          name: "Group chat",
          member_ids: selected,
        });
        onCreated(data);
      }
      onOpenChange(false);
      setSelected([]);
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-dm-dialog">
        <DialogHeader><DialogTitle>New message</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Pick one person for a DM, or several for a group chat.</p>
        <PersonPicker people={people} currentUser={currentUser} selected={selected} setSelected={setSelected} />
        <DialogFooter>
          <Button onClick={start} disabled={saving} data-testid="new-dm-submit" className="bg-[#255058] text-white hover:bg-[#1c3d43]">
            {selected.length > 1 ? "Start group" : "Start chat"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PollDialog({ open, onOpenChange, channelId, onCreated }) {
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [multi, setMulti] = useState(false);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const clean = options.map((o) => o.trim()).filter(Boolean);
    if (!question.trim() || clean.length < 2) {
      toast.error("Add a question and at least two options");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/channels/${channelId}/polls`, { question: question.trim(), options: clean, multi });
      toast.success("Poll posted");
      onOpenChange(false);
      setQuestion(""); setOptions(["", ""]); setMulti(false);
      onCreated();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="poll-dialog">
        <DialogHeader><DialogTitle>Create a poll</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Ask a question" data-testid="poll-question" />
          {options.map((opt, i) => (
            <Input
              key={i}
              value={opt}
              onChange={(e) => setOptions((prev) => prev.map((o, idx) => (idx === i ? e.target.value : o)))}
              placeholder={`Option ${i + 1}`}
              data-testid={`poll-option-input-${i}`}
            />
          ))}
          <Button variant="outline" size="sm" onClick={() => setOptions((prev) => [...prev, ""])}>
            <Plus className="mr-1 h-4 w-4" /> Add option
          </Button>
          <div className="flex items-center gap-2">
            <Switch id="poll-multi" checked={multi} onCheckedChange={setMulti} />
            <Label htmlFor="poll-multi">Allow multiple choices</Label>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="poll-submit" className="bg-[#255058] text-white hover:bg-[#1c3d43]">Post poll</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShoutoutDialog({ open, onOpenChange, channelId, people, badges, currentUser, onCreated }) {
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState("");
  const [badge, setBadge] = useState("team_player");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (selected.length === 0 || !message.trim()) {
      toast.error("Pick someone and write a message");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/channels/${channelId}/shoutout`, {
        to_user_ids: selected,
        message: message.trim(),
        badge,
      });
      toast.success("Shout-out sent 🎉");
      onOpenChange(false);
      setSelected([]); setMessage(""); setBadge("team_player");
      onCreated();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="shoutout-dialog">
        <DialogHeader><DialogTitle>Give a shout-out</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <PersonPicker people={people} currentUser={currentUser} selected={selected} setSelected={setSelected} />
          <div className="flex flex-wrap gap-2">
            {badges.map((b) => (
              <button
                key={b}
                onClick={() => setBadge(b)}
                data-testid={`shoutout-badge-${b}`}
                className={`rounded-full border px-3 py-1 text-xs ${
                  badge === b ? "border-[#255058] bg-[#255058]/10 font-semibold text-[#255058]" : "border-border"
                }`}
              >
                {BADGE_LABEL[b] || b}
              </button>
            ))}
          </div>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Recognize their great work…" data-testid="shoutout-message" />
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="shoutout-submit" className="bg-[#255058] text-white hover:bg-[#1c3d43]">Send shout-out</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
